import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useHomepageStats } from "@/lib/useHomepageStats";
import { TID } from "@/lib/testIds";
import PartnersChart from "@/components/PartnersChart";
// iter57 — CohortHighlights import removed; the Manifest deep section that
// consumed it was deleted as part of the homepage duplicate-content
// consolidation. The component still exists at /app/frontend/src/components/CohortHighlights.jsx
// for other future callers.
import { ArrowRight, Sparkles, Check, ChevronLeft, ChevronRight, ChevronDown, Info, Compass as CompassIcon, Users as UsersIcon, HeartHandshake, Package, BadgeCheck, Map as MapIcon } from "lucide-react";
import { EUROPE_COUNTRIES } from "@/data/europeMap";
import { flagFor } from "@/lib/flags";

/* ============================================================
   Reveal. One motion primitive used across the page.
   Fade + translate-up on first entry into the viewport.
   ============================================================ */
function Reveal({ children, delay = 0, y = 16, duration = 500, className = "" }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setShown(true); io.disconnect(); }
    }, { threshold: 0.15 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? "translateY(0)" : `translateY(${y}px)`,
        transition: `opacity ${duration}ms ease-out ${delay}ms, transform ${duration}ms ease-out ${delay}ms`,
        willChange: "opacity, transform",
      }}
    >
      {children}
    </div>
  );
}

/* ============================================================
   Matcher. Hero centrepiece.
   ============================================================ */
function Matcher({ homes, countries }) {
  const [home, setHome] = useState("");
  const [customHome, setCustomHome] = useState("");
  const [country, setCountry] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const isOther = home === "__other__";
  const effectiveHome = isOther ? customHome.trim() : home;

  useEffect(() => {
    if (!effectiveHome || !country) { setResult(null); return; }
    setLoading(true);
    api.get(`/match?home=${encodeURIComponent(effectiveHome)}&country=${encodeURIComponent(country)}`)
      .then((r) => setResult(r.data)).catch(() => setResult(null)).finally(() => setLoading(false));
  }, [effectiveHome, country]);

  const vr = result?.visa_rule;
  const line = vr && (
    <>
      <span className="font-bold">{vr.long_stay_visa || vr.short_stay_visa || "Long-stay visa"}</span>
      <span className="text-muted"> · fee {vr.fee || "–"} · {result.partner_count} partner{result.partner_count === 1 ? "" : "s"}</span>
    </>
  );

  return (
    <div className="w-full max-w-3xl mx-auto" data-testid="home-matcher">
      <div className="relative rounded-[24px] p-[2px] bg-[linear-gradient(135deg,var(--moe-ink)_0%,var(--moe-elec)_60%,var(--moe-ink)_100%)]">
        <div className="rounded-[22px] bg-paper p-6 md:p-8">
          <div className="flex items-center gap-2 mb-5">
            <span className="w-2 h-2 rounded-full bg-elec animate-pulse" />
            <div className="text-[10px] md:text-xs uppercase tracking-[0.2em] text-elec font-bold">Match me instantly</div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="moe-field">
              <select
                data-testid="match-home"
                value={home}
                onChange={(e) => setHome(e.target.value)}
                className="text-base"
                style={{ minHeight: 52 }}
              >
                <option value="">Home university…</option>
                {homes.map((h) => (<option key={h.short} value={h.short}>{h.short}</option>))}
                <option value="__other__">Other. Type your university</option>
              </select>
              <label>Home institution</label>
            </div>
            <div className="moe-field">
              <select
                data-testid="match-country"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                disabled={!effectiveHome}
                className="text-base"
                style={{ minHeight: 52 }}
              >
                <option value="">Destination country…</option>
                {countries.map((c) => (<option key={c.country_name} value={c.country_name}>{c.country_name}</option>))}
              </select>
              <label>Destination country</label>
            </div>
          </div>
          {isOther && (
            <div className="mt-4 moe-field">
              <input
                data-testid="match-home-other"
                type="text"
                value={customHome}
                onChange={(e) => setCustomHome(e.target.value)}
                placeholder=" "
                style={{ minHeight: 52 }}
              />
              <label>Type your university name</label>
            </div>
          )}
          <div className="mt-5 min-h-[28px] text-[15px] flex items-center gap-2" data-testid="match-summary">
            {!effectiveHome && !country && (
              <span className="text-muted text-sm">Pick both to see your visa route + partner count instantly.</span>
            )}
            {loading && <span className="inline-flex items-center gap-2 text-muted"><span className="w-2 h-2 rounded-full bg-elec animate-pulse" /> Matching…</span>}
            {!loading && result && line}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   ProofCycler. Rotates through 4 country facts every 4s.
   Also supports manual prev/next arrows.
   ============================================================ */
const PROOF_FACTS = [
  {
    id: "france", country: "France", tag: "elec",
    hero: "1 Aug 2026",
    line: "That's when France raises its funding requirement. Your timeline already knows.",
    source: "france-visas.gouv.fr",
  },
  {
    id: "italy", country: "Italy", tag: "yolk",
    hero: "8 days",
    line: "The window Italy gives you after landing to file for your residence permit.",
    source: "portaleimmigrazione.it",
  },
  {
    id: "austria", country: "Austria", tag: "pink",
    hero: "3 days",
    line: "Austria's Meldezettel. The mandatory address registration you owe after arrival.",
    source: "help.gv.at",
  },
  {
    id: "netherlands", country: "Netherlands", tag: "mint",
    hero: "EUR 15,588",
    line: "The proof-of-funds figure the Netherlands requires for a full academic year.",
    source: "ind.nl",
  },
];

const TAG_BG = {
  elec: "var(--moe-elec)", yolk: "var(--moe-yolk)",
  pink: "var(--moe-pink)", mint: "var(--moe-mint)",
};
const TAG_FG = { elec: "white", yolk: "var(--moe-ink)", pink: "var(--moe-ink)", mint: "var(--moe-ink)" };

function ProofCycler() {
  const [i, setI] = useState(0);
  const paused = useRef(false);      // hover pause
  const manualUntil = useRef(0);     // resume auto after this timestamp

  useEffect(() => {
    const t = setInterval(() => {
      if (paused.current) return;
      if (Date.now() < manualUntil.current) return;
      setI((v) => (v + 1) % PROOF_FACTS.length);
    }, 4500);
    return () => clearInterval(t);
  }, []);

  const markManual = () => { manualUntil.current = Date.now() + 6000; };
  const f = PROOF_FACTS[i];

  return (
    <div
      className="w-full text-center"
      onMouseEnter={() => (paused.current = true)}
      onMouseLeave={() => (paused.current = false)}
      data-testid="proof-cycler"
    >
      <div className="text-xs sm:text-sm uppercase tracking-[0.2em] text-muted font-bold">Precise, not vague</div>
      {/* country tag. Coloured badge */}
      <div className="mt-8 flex justify-center" key={`tag-${f.id}`}>
        <span
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] sm:text-xs font-bold uppercase tracking-widest border border-ink"
          style={{
            background: TAG_BG[f.tag], color: TAG_FG[f.tag],
            animation: "moe-fade-up 300ms ease-out",
          }}
          data-testid={`proof-tag-${f.id}`}
        >
          {f.country}
        </span>
      </div>
      <div
        key={f.id}
        className="mt-5 text-[72px] sm:text-[112px] md:text-[160px] lg:text-[192px] font-bold tracking-[-0.05em] leading-[0.9] text-ink px-2"
        data-testid={`proof-fact-${f.id}`}
        style={{ animation: "moe-fade-up 400ms ease-out" }}
      >
        {f.hero}
      </div>
      <p
        key={`c-${f.id}`}
        className="mt-8 mx-auto max-w-2xl text-lg sm:text-xl md:text-2xl text-muted leading-snug px-2"
        style={{ animation: "moe-fade-up 400ms ease-out 60ms both" }}
      >
        {f.line}
      </p>
      <div className="mt-6 text-xs text-soft uppercase tracking-widest whitespace-nowrap overflow-hidden text-ellipsis">
        Source: {f.source}
      </div>

      {/* dots + prev/next. Touch-friendly hit boxes */}
      <div className="mt-10 inline-flex items-center gap-4">
        <button
          aria-label="Previous"
          data-testid="proof-prev"
          onClick={() => { markManual(); setI((v) => (v - 1 + PROOF_FACTS.length) % PROOF_FACTS.length); }}
          className="w-11 h-11 flex items-center justify-center rounded-full text-ink/70 hover:text-elec hover:bg-tint transition-colors"
        ><ChevronLeft size={20} /></button>
        <div className="flex items-center gap-2">
          {PROOF_FACTS.map((_, idx) => (
            <button
              key={idx}
              aria-label={`Show fact ${idx + 1}`}
              onClick={() => { markManual(); setI(idx); }}
              className="p-2"
            >
              <span className="block transition-all"
                style={{
                  width: idx === i ? 28 : 8, height: 8, borderRadius: 999,
                  background: idx === i ? "var(--moe-elec)" : "rgba(10,10,10,0.25)",
                  transitionDuration: "250ms",
                }}
              />
            </button>
          ))}
        </div>
        <button
          aria-label="Next"
          data-testid="proof-next"
          onClick={() => { markManual(); setI((v) => (v + 1) % PROOF_FACTS.length); }}
          className="w-11 h-11 flex items-center justify-center rounded-full text-ink/70 hover:text-elec hover:bg-tint transition-colors"
        ><ChevronRight size={20} /></button>
      </div>
    </div>
  );
}

/* ============================================================
   EuropeMap. Full-bleed, dominant. 19 countries in accent.
   ============================================================ */
const HIGHLIGHT_ISO = ["FR","DE","IT","ES","BE","PL","CH","AT","NO","FI","NL","SE","GB","IE","CZ","HU","DK","PT","GR"];

function EuropeMap() {
  const ref = useRef(null);
  const navigate = useNavigate();
  const [lit, setLit] = useState(false);
  const [hover, setHover] = useState(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setLit(true); io.disconnect(); } }, { threshold: 0.2 });
    io.observe(el); return () => io.disconnect();
  }, []);
  return (
    <div className="w-full" data-testid="europe-map" ref={ref}>
      <div className="relative" onMouseLeave={() => setHover(null)}>
        <svg viewBox="0 0 1000 800" preserveAspectRatio="xMidYMid meet" className="w-full h-auto">
          {Object.entries(EUROPE_COUNTRIES).map(([iso, c]) => (
            <path key={`base-${iso}`} d={c.d} fill="rgba(10,10,10,0.06)" stroke="rgba(10,10,10,0.30)" strokeWidth="0.5" vectorEffect="non-scaling-stroke" />
          ))}
          {HIGHLIGHT_ISO.map((iso, idx) => {
            const c = EUROPE_COUNTRIES[iso]; if (!c) return null;
            return (
              <path
                key={`hl-${iso}`} d={c.d}
                fill={lit ? "var(--moe-elec)" : "transparent"}
                stroke="var(--moe-ink)" strokeWidth="0.8" vectorEffect="non-scaling-stroke"
                data-testid={`map-country-${iso}`}
                role="link"
                aria-label={`Open ${c.name} dossier`}
                // iter58 — staggered first-load animation (250ms per country vs
                // the previous 40ms). Hover / click behaviour is untouched — only
                // the entrance fill-in is slower and more visible. IntersectionObserver
                // fires once (io.disconnect after first hit), so repeat scrolls never
                // re-trigger and the map stays lit.
                style={{ transition: `fill 260ms ease-out ${idx * 250}ms`, cursor: "pointer" }}
                onClick={() => navigate(`/countries/${encodeURIComponent(c.name)}`)}
                onMouseEnter={(e) => {
                  const r = ref.current.getBoundingClientRect();
                  setHover(c.name); setPos({ x: e.clientX - r.left, y: e.clientY - r.top });
                }}
                onMouseMove={(e) => {
                  const r = ref.current.getBoundingClientRect();
                  setPos({ x: e.clientX - r.left, y: e.clientY - r.top });
                }}
              />
            );
          })}
        </svg>
        {hover && (
          <div style={{ position: "absolute", left: pos.x + 12, top: pos.y + 12, pointerEvents: "none" }}
            className="px-2.5 py-1 text-[11px] font-semibold uppercase tracking-widest bg-ink text-paper rounded-md whitespace-nowrap flex items-center gap-1.5"
            data-testid="map-hover-tooltip">
            <span aria-hidden="true" className="text-sm leading-none">{flagFor(hover)}</span>
            {hover} <span className="opacity-60 ml-1">↗</span>
          </div>
        )}
      </div>
      <p className="mt-3 text-center text-[11px] uppercase tracking-widest text-muted font-semibold">
        Click any highlighted country to open its dossier
      </p>
    </div>
  );
}

/* ============================================================
   PartnerWordmarks. Larger type per name.
   NOTE: text wordmarks pending confirmed logo-usage rights.
   Grid built to swap in real logo image assets later.
   ============================================================ */
const PARTNER_WORDMARKS = [
  { name: "HEC Paris",                                            country: "France" },
  { name: "IIM Ahmedabad",                                        country: "India" },
  { name: "London School of Economics",                           country: "United Kingdom" },
  { name: "ISB (Indian School of Business)",                      country: "India" },
  { name: "Bocconi University",                                   country: "Italy" },
  { name: "XLRI Jamshedpur",                                      country: "India" },
  { name: "WHU \u2013 Otto Beisheim School of Management",        country: "Germany" },
  { name: "IIM Bangalore",                                        country: "India" },
  { name: "IE Business School",                                   country: "Spain" },
  { name: "MDI Gurgaon",                                          country: "India" },
  { name: "KU Leuven",                                            country: "Belgium" },
  { name: "IIM Calcutta",                                         country: "India" },
  { name: "University of St. Gallen",                             country: "Switzerland" },
  { name: "SPJIMR",                                               country: "India" },
  { name: "Rotterdam School of Management (Erasmus University)",  country: "Netherlands" },
  { name: "FMS Delhi",                                            country: "India" },
  { name: "Stockholm School of Economics",                        country: "Sweden" },
  { name: "IIFT Delhi",                                           country: "India" },
  { name: "Imperial College London",                              country: "United Kingdom" },
  { name: "WU Vienna",                                            country: "Austria" },
  { name: "Norwegian School of Economics (NHH)",                  country: "Norway" },
  { name: "London Business School",                               country: "United Kingdom" },
  { name: "Aalto University",                                     country: "Finland" },
  { name: "UCD Michael Smurfit Graduate Business School",         country: "Ireland" },
  { name: "Corvinus University of Budapest",                      country: "Hungary" },
  { name: "Copenhagen Business School",                           country: "Denmark" },
];
function PartnerWordmarks() {
  // iter58 — infinite horizontal marquee, subtle + slow (60s loop, `moe-marquee`
  // keyframe already defined in /app/frontend/src/index.css). Pauses on hover
  // so users can read a specific name. Renders the SAME 26 real university
  // names as before — no new data, no fake logos. Duplicated inline so the
  // -50% translate keyframe loops seamlessly. Falls back to `prefers-reduced-motion`
  // via CSS (see index.css).
  return (
    <div
      className="relative overflow-hidden border border-ink/15 rounded-2xl bg-paper py-8 md:py-10"
      data-testid="partner-wordmarks"
    >
      {/* Edge fades so wordmarks appear to melt in/out on both sides */}
      <div className="pointer-events-none absolute inset-y-0 left-0 w-16 md:w-24 z-10"
           style={{background: "linear-gradient(to right, var(--moe-paper), transparent)"}} />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-16 md:w-24 z-10"
           style={{background: "linear-gradient(to left, var(--moe-paper), transparent)"}} />

      <div
        className="moe-marquee-track group"
        data-testid="partner-marquee-track"
        style={{ animationDuration: "60s" }}
        onMouseEnter={(e) => { e.currentTarget.style.animationPlayState = "paused"; }}
        onMouseLeave={(e) => { e.currentTarget.style.animationPlayState = "running"; }}
      >
        {[...PARTNER_WORDMARKS, ...PARTNER_WORDMARKS].map(({ name, country }, i) => (
          <span
            key={`${name}-${i}`}
            className="shrink-0 inline-flex items-center gap-3 whitespace-nowrap"
            title={country}
          >
            <span className="text-[16px] md:text-[18px] lg:text-[20px] font-bold tracking-[-0.015em] text-ink hover:text-elec transition-colors">
              {name}
            </span>
            <span aria-hidden="true" className="text-ink/25 text-2xl leading-none select-none">·</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/* ============================================================
   Dashboard mockup. Larger, anchors a two-column layout with
   one line of copy on the side.
   ============================================================ */
function DashboardMockup() {
  return (
    <div
      className="border border-ink rounded-2xl bg-paper overflow-hidden max-w-full"
      style={{ boxShadow: "0 60px 100px -30px rgba(10,10,10,0.35), 0 20px 40px -12px rgba(10,10,10,0.20)" }}
      data-testid="dashboard-mockup"
    >
      <div className="flex items-center gap-2 px-5 py-3.5 border-b border-ink/15 bg-black/[0.03]">
        <span className="w-3 h-3 rounded-full bg-[#ff5f57] border border-black/10 shrink-0" />
        <span className="w-3 h-3 rounded-full bg-[#febc2e] border border-black/10 shrink-0" />
        <span className="w-3 h-3 rounded-full bg-[#28c840] border border-black/10 shrink-0" />
        <div className="ml-3 text-[11px] md:text-[12px] text-muted tabular-nums truncate min-w-0">ministry-of-exchange.app / dashboard</div>
      </div>
      <div className="p-5 sm:p-7 md:p-10 min-w-0">
        <div className="text-[11px] uppercase tracking-widest text-muted font-bold">Your case</div>
        <h3 className="mt-2 text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight break-words">ISB → HEC Paris · Fall 2026</h3>
        <div className="mt-7 grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2 moe-panel-elec p-5 sm:p-6 text-white min-w-0">
            <div className="text-[10px] uppercase tracking-widest opacity-70 font-bold">Long-stay visa</div>
            <div className="mt-2 text-lg sm:text-xl md:text-2xl font-bold leading-tight break-words">Étudiant · mobilité (VLS-TS)</div>
            <div className="mt-5 flex items-baseline gap-5 sm:gap-8 flex-wrap">
              <div><div className="text-[10px] uppercase tracking-widest opacity-60 font-bold">Fee</div><div className="text-base sm:text-lg font-bold">EUR 50</div></div>
              <div><div className="text-[10px] uppercase tracking-widest opacity-60 font-bold">Funds</div><div className="text-base sm:text-lg font-bold">EUR 615/mo</div></div>
              <div><div className="text-[10px] uppercase tracking-widest opacity-60 font-bold">Processing</div><div className="text-base sm:text-lg font-bold">15 days</div></div>
            </div>
          </div>
          <div className="moe-panel-yolk p-5 sm:p-6 min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-bold">Progress</div>
            <div className="mt-2 text-3xl sm:text-4xl font-bold tabular-nums">6 / 11</div>
            <div className="mt-4 h-2 rounded-full bg-black/20 overflow-hidden"><div className="h-full bg-ink" style={{ width: "54%" }} /></div>
            <div className="mt-3 text-[11px] font-semibold">Next: biometrics · Fri</div>
          </div>
        </div>
        <div className="mt-7 grid md:grid-cols-2 gap-x-10 gap-y-3 text-[14px] sm:text-[15px]">
          {[
            { d:true,  t:"Admission letter received" },
            { d:true,  t:"Campus France · account created" },
            { d:true,  t:"Passport valid ≥ 3 months post-stay" },
            { d:true,  t:"Accommodation booking confirmed" },
            { d:true,  t:"Bank statement (last 3 months)" },
            { d:true,  t:"Visa fee paid" },
            { d:false, t:"VFS biometrics appointment", chip:"Fri" },
            { d:false, t:"OFII form completed" },
            { d:false, t:"Return flight reservation" },
            { d:false, t:"Travel + medical insurance" },
            { d:false, t:"Register with prefecture (post-arrival)", chip:"Aug 12" },
          ].map((t, i) => (
            <div key={i} className="flex items-center gap-3 min-w-0">
              <span className={`w-5 h-5 rounded-full border border-ink flex items-center justify-center shrink-0 ${t.d ? "bg-[color:var(--moe-elec)]" : "bg-transparent"}`}>
                {t.d && <Check size={12} strokeWidth={3} className="text-white" />}
              </span>
              <span className={`${t.d ? "line-through text-muted" : "text-ink"} truncate min-w-0`}>{t.t}</span>
              {t.chip && <span className="ml-auto shrink-0 text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 border border-ink rounded-full bg-[color:var(--moe-yolk)]">{t.chip}</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   CommunityGraph. Large network with animated drawing lines.
   Lines are drawn on scroll-into-view using stroke-dasharray.
   ============================================================ */
function CommunityGraph() {
  const nodes = [
    { x: 22, y: 26, c: "var(--moe-mint)", i: "A", city: "Paris" },
    { x: 38, y: 46, c: "var(--moe-pink)", i: "P", city: "Milan" },
    { x: 55, y: 20, c: "var(--moe-yolk)", i: "K", city: "Berlin" },
    { x: 72, y: 40, c: "var(--moe-cyan)", i: "R", city: "Vienna" },
    { x: 86, y: 26, c: "var(--moe-elec)", i: "S", city: "Prague", light: true },
  ];
  // pairs to connect (subset — not every pair, keeps it clean)
  const pairs = [[0,2],[2,4],[0,1],[1,3],[2,3],[3,4]];
  const ref = useRef(null);
  const [drawn, setDrawn] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setDrawn(true); io.disconnect(); } }, { threshold: 0.3 });
    io.observe(el); return () => io.disconnect();
  }, []);
  return (
    <div className="w-full" ref={ref} data-testid="community-graph">
      <div className="relative aspect-[16/9] border border-ink rounded-[22px] bg-paper overflow-hidden">
        <div className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage: "linear-gradient(rgba(10,10,10,1) 1px, transparent 1px), linear-gradient(90deg, rgba(10,10,10,1) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
          }}
        />
        <svg viewBox="0 0 100 56" preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
          {pairs.map(([a, b], i) => {
            const A = nodes[a], B = nodes[b];
            const dx = B.x - A.x, dy = B.y - A.y;
            const len = Math.sqrt(dx*dx + dy*dy);
            return (
              <line
                key={i} x1={A.x} y1={A.y} x2={B.x} y2={B.y}
                stroke="var(--moe-ink)" strokeOpacity="0.35" strokeWidth="0.25"
                strokeDasharray={len} strokeDashoffset={drawn ? 0 : len}
                style={{ transition: `stroke-dashoffset 900ms ease-out ${i * 120}ms` }}
              />
            );
          })}
        </svg>
        {nodes.map((n) => (
          <div key={n.i} className="absolute" style={{ left: `${n.x}%`, top: `${(n.y / 56) * 100}%`, transform: "translate(-50%,-50%)" }}>
            <div
              className="w-14 h-14 md:w-20 md:h-20 rounded-full border-2 border-ink flex items-center justify-center text-base md:text-xl font-bold"
              style={{ background: n.c, color: n.light ? "white" : "var(--moe-ink)" }}
            >{n.i}</div>
            <div className="mt-2 text-[11px] md:text-xs tracking-widest uppercase font-bold text-ink text-center">{n.city}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============================================================
   MapInfoBadge. Unobtrusive tap/hover-trigger disclosing that
   the 17 covered countries include the EU, Schengen, plus UK &
   Ireland. Kept out of the primary caption.
   ============================================================ */
function MapInfoBadge() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const close = (e) => { if (!ref.current || !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [open]);
  return (
    <span className="relative inline-flex" ref={ref}>
      <button
        type="button"
        aria-label="What's included"
        data-testid="map-whats-included"
        onClick={() => setOpen(true)}
        onMouseEnter={() => setOpen(true)}
        className="w-11 h-11 md:w-9 md:h-9 flex items-center justify-center rounded-full border border-ink text-ink hover:bg-tint transition-colors"
      >
        <Info size={16} />
      </button>
      {open && (
        <span
          role="tooltip"
          data-testid="map-whats-included-tooltip"
          className="absolute left-1/2 -translate-x-1/2 top-[calc(100%+8px)] w-max max-w-[280px] px-3 py-2 text-[13px] font-semibold text-paper bg-ink rounded-md leading-snug z-30"
        >
          Includes the EU, the Schengen area, and the UK &amp; Ireland.
        </span>
      )}
    </span>
  );
}

/* ============================================================
   CountUp. Counts from 0 → end on first scroll into view.
   ============================================================ */
function CountUp({ end, duration = 800 }) {
  const [n, setN] = useState(0);
  const ref = useRef(null);
  const done = useRef(false);
  useEffect(() => {
    const el = ref.current; if (!el || done.current) return;
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting || done.current) return;
      done.current = true;
      const start = performance.now();
      const tick = (t) => {
        const p = Math.min(1, (t - start) / duration);
        const eased = 1 - Math.pow(1 - p, 3);
        setN(Math.round(end * eased));
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }, { threshold: 0.35 });
    io.observe(el); return () => io.disconnect();
  }, [end, duration]);
  return <span ref={ref} className="tabular-nums">{n}</span>;
}

/* ============================================================
   iter58. HeroTimelineTeaser
   Small animated glimpse of the real Compass task-board style,
   placed under the Matcher form. 3 task rows tick off in
   sequence on a loop. Card treatment mirrors the actual
   dashboard task cards (see /app/frontend/src/pages/Dashboard.jsx
   task list) so it reads as a genuine product glimpse.
   ============================================================ */
function HeroTimelineTeaser() {
  const TASKS = [
    { label: "Blocked account confirmation (Sperrkonto)", stage: "Before you fly" },
    { label: "Anmeldung. Address registration",          stage: "First 12 days" },
    { label: "Activate statutory health insurance",       stage: "First 12 days" },
  ];
  const [step, setStep] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setStep((s) => (s >= TASKS.length ? 0 : s + 1));
    }, 1400);
    return () => clearInterval(id);
  }, []);
  return (
    <div
      className="mt-8 mx-auto max-w-md rounded-2xl border border-ink/12 bg-paper shadow-[0_1px_0_rgba(10,10,10,0.03),0_8px_28px_-14px_rgba(10,10,10,0.14)] overflow-hidden"
      data-testid="hero-timeline-teaser"
      aria-hidden="true"
    >
      <div className="px-4 pt-3.5 pb-2 border-b border-ink/8 flex items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-[0.2em] text-soft font-bold">A real slice of Northstar</div>
        <div className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-elec animate-pulse"/>
          <span className="text-[10px] uppercase tracking-widest text-muted font-bold tabular-nums">Berlin · 140d</span>
        </div>
      </div>
      <ul className="divide-y divide-ink/8">
        {TASKS.map((t, i) => {
          const done = i < step;
          return (
            <li key={t.label} className="px-4 py-3 flex items-center gap-3 text-left">
              <span
                className={`w-4 h-4 rounded-full shrink-0 border flex items-center justify-center transition-colors duration-300 ${done ? "bg-elec border-elec" : "border-ink/25 bg-transparent"}`}
                data-testid={`hero-teaser-check-${i}`}
              >
                {done && (
                  <svg viewBox="0 0 12 12" className="w-2.5 h-2.5 text-white" style={{animation: "moe-draw 240ms ease-out"}}>
                    <path d="M3 6.5 L5 8.5 L9 4" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </span>
              <div className="min-w-0 flex-1">
                <div className={`text-[13px] leading-tight font-semibold transition-colors ${done ? "text-ink/50 line-through decoration-ink/30" : "text-ink"}`}>
                  {t.label}
                </div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted font-bold mt-0.5">{t.stage}</div>
              </div>
            </li>
          );
        })}
      </ul>
      <div className="px-4 py-2.5 bg-ink/[0.02] border-t border-ink/8">
        <div className="flex items-center justify-between gap-2 text-[10px] uppercase tracking-widest text-muted font-bold">
          <span>Live personalised timeline · not a demo</span>
          <span className="tabular-nums text-ink font-black">{step}/{TASKS.length}</span>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   iter58. RecentlyVerifiedTicker
   Honest rotator of country_playbooks reverification dates.
   Only surfaces countries within the backend's fresh-days
   threshold (default 90d). Never fabricates a date, never
   pads with stale entries. If the backend returns an empty
   list, the ticker renders nothing (returns null).
   ============================================================ */
function RecentlyVerifiedTicker() {
  const [items, setItems] = useState(null); // null = loading, [] = nothing fresh, list = show
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    api.get("/homepage/recently-verified")
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]));
  }, []);

  useEffect(() => {
    if (!items || items.length < 2) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % items.length), 3500);
    return () => clearInterval(id);
  }, [items]);

  if (!items || items.length === 0) return null;

  const fmt = (iso) => {
    try {
      const d = new Date(iso + "T00:00:00");
      return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    } catch { return iso; }
  };
  const current = items[idx % items.length];

  return (
    <div
      className="mt-6 flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.18em] text-muted font-bold"
      data-testid="recently-verified-ticker"
      aria-live="polite"
    >
      <span className="w-1.5 h-1.5 rounded-full bg-elec animate-pulse" aria-hidden="true"/>
      <span className="text-elec">Recently verified</span>
      <span className="text-ink/25">·</span>
      <span
        key={current.country}
        className="text-ink normal-case tracking-tight font-semibold"
        style={{ animation: "moe-fade-up 400ms ease-out" }}
        data-testid={`recently-verified-item-${current.country.toLowerCase()}`}
      >
        {current.country} <span className="text-muted">reverified {fmt(current.last_verified_date)}</span>
      </span>
    </div>
  );
}

/* ============================================================
   Page
   ============================================================ */
export default function Home() {
  const stats = useHomepageStats();
  const [countries, setCountries] = useState([]);
  const [homes, setHomes] = useState([]);
  useEffect(() => {
    api.get("/countries").then((r) => setCountries(r.data.countries)).catch(() => {});
    api.get("/partnerships/home-universities").then((r) => setHomes(r.data.home_universities)).catch(() => {});
  }, []);

  return (
    <div className="bg-paper text-ink">

      {/* ================ 1 · HERO (centered, large) ================ */}
      <section className="pt-24 pb-28 md:pt-32 md:pb-36">
        <div className="max-w-6xl mx-auto w-full px-6 text-center">
          <Reveal>
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-muted font-semibold">
              <Sparkles size={11} className="text-elec" />
              Exchange & internships · Europe
            </div>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="mt-8 mx-auto max-w-5xl text-[52px] sm:text-7xl md:text-[88px] lg:text-[112px] font-bold tracking-[-0.05em] leading-[0.94]">
              Everything after the offer letter.
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="mt-8 text-base md:text-lg text-muted max-w-2xl mx-auto">
              Visa, timeline, and the people going through it with you.
            </p>
          </Reveal>
          <Reveal delay={260}>
            <div className="mt-12"><Matcher homes={homes} countries={countries} /></div>
          </Reveal>
          <Reveal delay={300}>
            <HeroTimelineTeaser />
          </Reveal>
          <Reveal delay={320}>
            <a
              href="#section-manifest"
              data-testid="hero-manifest-hook"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector("#section-manifest")?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className="mt-6 inline-flex items-center gap-2 text-[12px] md:text-sm font-semibold text-muted hover:text-elec transition-colors group"
            >
              <UsersIcon size={14} className="text-elec" strokeWidth={2.2} />
              <span>Not just a checklist. See who else is headed your way</span>
              <ChevronDown size={14} className="text-elec transition-transform group-hover:translate-y-0.5" strokeWidth={2.2} />
            </a>
          </Reveal>
          <Reveal delay={360}>
            <div className="mt-10">
              <Link to="/start" data-testid={TID.heroCta} className="moe-btn moe-btn-primary !px-8 !py-3.5 text-base">
                Start <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ================ 1b · PRODUCT OVERVIEW STRIP (Compass · Manifest · Smart Packing · Concierge · Roam) ================
           Positioned immediately after the hero so a first-time visitor sees ALL five products
           within 5-10 seconds of landing. Each card is a shallow signpost that jumps down to the
           full existing section further down the page.

           ⚠️ HOMEPAGE TEXT DISCIPLINE. READ BEFORE ADDING A 6TH PRODUCT
           ┌─────────────────────────────────────────────────────────────────────────────────────┐
           │ Product-strip one-liners  →  MAX 8 words each.                                       │
           │ Product-Proof sub-lines   →  MAX 12 words each (see /* section 5 comment below).     │
           │                                                                                      │
           │ This strip has been trimmed multiple times as new products were added (Concierge in  │
           │ iter45, Roam in iter67). Each expansion started with well-intentioned copy that      │
           │ exceeded these limits and produced a "wall of stacked text" on mobile. 10+ lines    │
           │ before the visitor sees any real product proof. Iter68 added the mobile snap-carousel│
           │ AND enforces these word-count rules so the strip stays scannable in <5 seconds.      │
           │                                                                                      │
           │ ANY future product addition MUST hit these limits. No exemptions "just this once".  │
           │ If your copy needs more than 8 words to communicate the value, the product name is   │
           │ probably wrong.                                                                      │
           └─────────────────────────────────────────────────────────────────────────────────────┘

           MOBILE STRUCTURE. Iter68 fix: below sm (≤639px) the strip becomes a horizontally
           swipeable snap-carousel (5 cards × ~85vw each with a native edge peek), not a stacked
           list. This turns a scroll-heavy wall of text into a natural mobile browse pattern.
           sm+ keeps the existing grid layout (2/3/5 cols) unchanged. */}
      <section
        className="py-16 md:py-20 border-t border-ink/10"
        data-testid="product-overview-strip"
      >
        <div className="max-w-7xl mx-auto w-full px-6">
          <Reveal>
            <div className="text-xs uppercase tracking-[0.2em] text-muted font-bold">Five products, one platform</div>
          </Reveal>
          {/* mobile: -mx-6 + px-6 lets the flex track break OUT of the section padding so the
              first card's left edge sits at 24px AND the last card's right edge sits at 24px,
              while everything in between can scroll edge-to-edge. Sm+: revert to the grid. */}
          <div
            className="
              mt-6 -mx-6 px-6 flex overflow-x-auto snap-x snap-mandatory gap-4 pb-2
              [scrollbar-width:none] [&::-webkit-scrollbar]:hidden
              sm:mx-0 sm:px-0 sm:overflow-visible sm:snap-none sm:gap-5
              sm:grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 md:gap-6
            "
            data-testid="product-strip-track"
          >
            {[
              {
                key: "compass",
                icon: CompassIcon,
                label: "Northstar",
                // iter68 — one-liner tightened to ≤8 words. Do NOT expand.
                headline: "Never miss a visa deadline.",
                // iter87 (Item Y, re-scoped) — plain-language use-case line.
                // Placed next to the product image, not on the hero.
                usecase: "See exactly what your visa needs, sourced from the government.",
                href: "/#section-compass",
                testid: "product-card-compass",
                accentVar: "var(--moe-elec)",
                accentTintClass: "hover:bg-[color:var(--moe-elec)]/[0.04]",
              },
              {
                key: "manifest",
                icon: UsersIcon,
                label: "Cohort",
                headline: "Find your people. Plan real trips.",
                usecase: "Meet your cohort before you land - and coordinate trips, arrivals, and roommate hunts together.",
                href: "/#section-manifest",
                testid: "product-card-manifest",
                accentVar: "var(--moe-pink)",
                accentTintClass: "hover:bg-[color:var(--moe-pink)]/[0.12]",
              },
              {
                key: "packing",
                icon: Package,
                label: "Smart Packing",
                headline: "Know exactly what to bring.",
                usecase: "A packing list built from your flight, city, and season.",
                href: "/#section-packing",
                testid: "product-card-packing",
                accentVar: "var(--moe-yolk)",
                accentTintClass: "hover:bg-[color:var(--moe-yolk)]/[0.22]",
              },
              {
                key: "roam",
                icon: MapIcon,
                label: "Drift",
                headline: "Weekend trips your budget allows.",
                usecase: "Weekend travel plans built around your host city.",
                href: "/#section-roam",
                testid: "product-card-roam",
                accentVar: "var(--moe-mint)",
                accentTintClass: "hover:bg-[color:var(--moe-mint)]/[0.20]",
              },
              {
                key: "concierge",
                icon: BadgeCheck,
                label: "Concierge",
                headline: "A real person handles it for you.",
                usecase: "For when you'd rather someone else book the appointment and chase the paperwork.",
                href: "/pricing",
                testid: "product-card-concierge",
                accentVar: "var(--moe-ink)",
                accentTintClass: "hover:bg-ink/[0.04]",
              },
            ].map((p, i) => (
              <Reveal key={p.key} delay={80 * (i + 1)} y={18} className="snap-start shrink-0 w-[85%] sm:w-auto sm:shrink">
                <a
                  href={p.href}
                  data-testid={p.testid}
                  data-accent={p.key}
                  className={`group relative flex flex-col h-full rounded-2xl border border-ink/12 p-6 md:p-7 overflow-hidden transition-colors hover:border-ink/25 ${p.accentTintClass}`}
                >
                  {/* Thin accent bar at top. Unique color per product, but
                      identical thickness and placement so cards stay a family.
                      Decorative only. No text sits on this bar. */}
                  <span
                    aria-hidden="true"
                    className="absolute top-0 left-0 right-0 h-[3px]"
                    style={{ background: p.accentVar }}
                  />
                  <div className="flex items-center gap-2.5">
                    {/* iter59. Icon + label use text-ink (19:1 contrast on
                        paper) instead of the accent color. Pink/yolk labels
                        previously failed AA. */}
                    <p.icon size={18} strokeWidth={2} className="text-ink" />
                    <div className="text-xs uppercase tracking-[0.2em] font-bold text-ink">{p.label}</div>
                  </div>
                  <div className="mt-4 text-xl md:text-2xl font-bold tracking-tight leading-[1.15] text-ink">
                    {p.headline}
                  </div>
                  {/* iter87 (Item Y, re-scoped) - plain-language use-case
                      line lives right under the headline. Muted, small,
                      leading-snug so it reads as sub-copy, not a competing
                      headline. flex-1 moves to the wrapper below so the CTA
                      still pins to the card bottom. */}
                  <div
                    className="mt-2 text-[13px] text-ink/70 leading-snug flex-1"
                    data-testid={`${p.testid}-usecase`}
                  >
                    {p.usecase}
                  </div>
                  <div className="mt-5 inline-flex items-center gap-1.5 text-xs font-bold text-ink group-hover:text-elec transition-colors">
                    Learn more <ArrowRight size={13} className="transition-transform group-hover:translate-x-0.5" />
                  </div>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ================ 2 · PROOF CYCLER (tinted rhythm) ================ */}
      <section className="py-24 md:py-32 border-t border-ink/10 bg-tint">
        <div className="max-w-6xl mx-auto w-full px-6">
          <Reveal><ProofCycler /></Reveal>
        </div>
      </section>

      {/* ================ 3 · EUROPE MAP (full-bleed, dominant) ================ */}
      <section className="py-24 md:py-32 border-t border-ink/10">
        <div className="max-w-[1600px] mx-auto w-full px-4 md:px-8">
          <Reveal y={20}><EuropeMap /></Reveal>
          <Reveal delay={200}>
            <div className="mt-10 flex items-center justify-center gap-3 flex-wrap">
              <p className="text-center text-xl md:text-3xl font-bold tracking-[-0.02em]">
                19 countries. One platform.
              </p>
              <MapInfoBadge />
            </div>
          </Reveal>
          <Reveal delay={300}>
            <p className="mt-3 text-center text-xs md:text-sm uppercase tracking-[0.2em] text-muted font-bold">
              {stats ? `${stats.partner_universities} partner universities` : "Partner universities"}
              {stats?.roam_host_cities ? (
                <>
                  {/* iter67. Roam anchor coverage promoted to homepage as a
                      trust signal, live-computed from roam.ANCHOR_CITIES so it
                      stays honest as coverage expands. Rendered as a peer of
                      the partner-universities line to feel like one stat
                      family, not two separated claims. */}
                  <span className="mx-3 text-ink/30" aria-hidden="true">·</span>
                  <span data-testid="stat-roam-host-cities">
                    {stats.roam_host_cities} real host cities in Drift
                  </span>
                </>
              ) : null}
            </p>
          </Reveal>
          {/* iter58. Honest reverification ticker: only renders when the
              backend returns countries within the fresh-days window. If the
              known stale-data gap widens, this quietly hides itself rather
              than exposing bad dates on the homepage. */}
          <Reveal delay={380}>
            <RecentlyVerifiedTicker />
          </Reveal>
        </div>
      </section>

      {/* ================ 4 · PARTNER WORDMARKS (tinted; headline + CTA) ================ */}
      <section className="py-24 md:py-32 border-t border-ink/10 bg-tint">
        <div className="max-w-7xl mx-auto w-full px-6">
          <Reveal>
            <div className="text-center">
              <div className="text-xs md:text-sm uppercase tracking-[0.2em] text-muted font-bold">A preview of the network</div>
              <h2 className="mt-3 text-3xl md:text-5xl lg:text-6xl font-bold tracking-[-0.03em] leading-[1.02]">
                {stats ? `${stats.partner_universities} partner universities.` : "Partner universities."}<br className="hidden md:block"/> These are 26 of them.
              </h2>
            </div>
          </Reveal>
          <Reveal delay={140} y={20}>
            <div className="mt-12 md:mt-16"><PartnerWordmarks /></div>
          </Reveal>
          <Reveal delay={220}>
            <div className="mt-10 text-center">
              <Link
                to="/universities"
                data-testid="partners-explore-all"
                className="moe-btn moe-btn-ghost !px-6 !py-3 text-base"
              >
                Explore all {stats ? stats.partner_universities : ""} partner universities <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ================ 5 · COMPASS. The product (two-column asymmetric) ================ */}
      {/* ================ 5 · PRODUCT PROOF. 5 alternating panels with real screenshots ================
           Real screenshots of the actual working product, benefit-first headlines. Each panel alternates
           layout (screenshot left / right / left / right) so the section has visual rhythm. Uses the
           existing Reveal entrance-fade-up motion.

           ⚠️ HOMEPAGE TEXT DISCIPLINE. READ BEFORE ADDING A 6TH PANEL
           Product-Proof sub-lines ≤12 words each (see also the strip discipline comment above).
           On mobile the real screenshot IS the primary proof. The sub-line should support it, not
           duplicate what the image already communicates. If a sub-line needs more than 12 words,
           either the screenshot is doing too little or the headline is trying to say too much. */}
      <section className="py-24 md:py-32 border-t border-ink/10" data-testid="product-proof">
        <div className="max-w-7xl mx-auto w-full px-6 space-y-24 md:space-y-32">
          {[
            {
              key: "compass",
              label: "Northstar",
              headline: "Never miss the deadline that costs you the exchange.",
              // iter68 — sub-line ≤12 words. Screenshot carries the rest.
              sub: "Personalised timeline sourced from official government portals. Not blog posts.",
              img: "/marketing/compass.png",
              alt: "Northstar dashboard showing Germany's official checklist, next-appointment window, and 22 June 2026 Sperrbestätigung policy update.",
              flip: false,
            },
            {
              key: "manifest",
              label: "Cohort + Assembly",
              headline: "Match with your cohort. Then actually plan the trip together. Not just talk about it.",
              sub: "Match by host city, university, and consular jurisdiction. Coordinate real trips together.",
              img: "/marketing/moments.png",
              alt: "Cohort Assembly tab showing four coordinated trips including a confirmed 'Prague weekend' with 4 of 6 going and a locked October date.",
              flip: true,
            },
            {
              key: "packing",
              label: "Smart Packing",
              headline: "Stop guessing what to pack. Built from your exact dates, country, and airline.",
              sub: "Personalised by climate, accommodation type, and your exact airline's cabin-bag allowance.",
              img: "/marketing/packing.png",
              alt: "Smart Packing setup form pre-filled with Germany, Berlin, Sep 15 2026 arrival, and questions about bedding, towels, and kitchen access at destination.",
              flip: false,
            },
            {
              key: "concierge",
              label: "Concierge",
              headline: "When it's too much to handle alone, a real person takes it off your plate.",
              sub: "Documents, VFS booking, interview prep. One named handler, no upfront payment.",
              img: "/marketing/concierge.png",
              alt: "Pricing page showing the Concierge tier. ₹19,500 one-time, named case handler, with the full list of what's actually done for you.",
              flip: true,
            },
            {
              // iter67 — Roam added as the 5th product-proof panel. flip=false
              // continues the same visual rhythm (screenshot-right / left /
              // right / left / right) already used by the first four panels.
              key: "roam",
              label: "Drift",
              headline: "You'll only get so many weekends here. Don't waste them wondering where to go.",
              sub: "Real destinations, real budgets. Plan the trip with matched peers.",
              img: "/marketing/roam.png",
              alt: "Drift page for a Paris-based student showing 5 curated weekend destinations (Jouy-en-Josas, Évry-Courcouronnes, Cergy, Reims, Troyes) with real distances, real cost ranges, and a Trip-Intent toggle on each card.",
              flip: false,
            },
          ].map((p) => (
            <div key={p.key} id={`section-${p.key}`} data-testid={`product-proof-${p.key}`} className="grid lg:grid-cols-12 gap-8 lg:gap-14 items-center scroll-mt-24">
              <Reveal y={20} className={`lg:col-span-6 ${p.flip ? "lg:order-2" : ""}`}>
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-elec font-bold">{p.label}</div>
                  <h3 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold tracking-[-0.02em] leading-[1.08] text-ink">
                    {p.headline}
                  </h3>
                  <p className="mt-5 text-base md:text-lg text-muted leading-relaxed max-w-xl">
                    {p.sub}
                  </p>
                </div>
              </Reveal>
              <Reveal y={20} delay={120} className={`lg:col-span-6 ${p.flip ? "lg:order-1" : ""}`}>
                <div className="rounded-2xl overflow-hidden border border-ink/12 shadow-[0_20px_60px_-30px_rgba(0,0,0,0.35)] bg-white">
                  <img
                    src={p.img}
                    alt={p.alt}
                    loading="lazy"
                    className="w-full h-auto block"
                    data-testid={`product-proof-img-${p.key}`}
                  />
                </div>
              </Reveal>
            </div>
          ))}
        </div>
      </section>

      {/* ================ 6b · PARTNERS CHART. Live data-viz (paper for rhythm) ================ */}
      <section className="py-24 md:py-32 border-t border-ink/10" data-testid="section-partners-chart">
        <div className="max-w-6xl mx-auto w-full px-4 sm:px-6">
          <Reveal>
            <div className="inline-flex items-center gap-2 text-xs md:text-sm uppercase tracking-[0.2em] text-elec font-bold">
              <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--moe-elec)]" />
              The data behind 163
            </div>
          </Reveal>
          <Reveal delay={60}>
            <h2 className="mt-4 text-3xl md:text-5xl lg:text-6xl font-bold tracking-[-0.03em] leading-[1.02]">
              Where our students<br />actually go.
            </h2>
          </Reveal>
          <Reveal delay={140}>
            <p className="mt-5 max-w-2xl text-base md:text-lg text-muted leading-relaxed">
              Not a curated highlight reel. Every partnership below is a verified row in the master dataset, sorted by unique host universities per country.
            </p>
          </Reveal>
          <Reveal delay={220} y={24}>
            <div className="mt-10">
              <PartnersChart limit={8} />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ================ 7 · FOUNDER (asymmetric. Big 560 numeral | founder portrait + copy) ================ */}
      {/* iter58. The 560 numeral now overlaps the portrait's left edge on
          md+ so the number and the photo read as one visual unit rather than
          two disconnected columns. Below md the layout stacks and the numeral
          sits ABOVE the portrait (no negative-margin overlap, to keep mobile
          readable). Copy on the right is unchanged. */}
      <section className="py-28 md:py-40 border-t border-ink/10" data-testid="founder-section">
        <div className="max-w-7xl mx-auto w-full px-6">
          <div className="grid md:grid-cols-12 gap-12 md:gap-4 items-center">
            {/* LEFT. Giant 560 numeral, pulled toward the portrait on md+ */}
            <div className="md:col-span-4 md:pr-0 md:-mr-8 lg:-mr-16 relative z-10">
              <Reveal>
                <div
                  className="text-[144px] sm:text-[220px] md:text-[240px] lg:text-[300px] font-black tracking-[-0.07em] leading-[0.82] text-ink"
                  data-testid="founder-numeral"
                >
                  <CountUp end={560} duration={900} />
                </div>
              </Reveal>
            </div>

            {/* RIGHT. Portrait + full headline + paragraph */}
            <div className="md:col-span-8">
              <div className="flex flex-col md:flex-row gap-8 md:gap-10 items-start">
                {/* Portrait. Anchors visually against the 560 numeral's edge */}
                <Reveal delay={80} y={20} className="w-full max-w-[280px] md:max-w-none md:w-[42%] shrink-0 relative">
                  <img
                    src="https://customer-assets-rejwkqb3.emergentagent.net/job_visa-compass-20/artifacts/rcoq46zu_founder-portrait.jpg"
                    alt="Nikhilesh, Founder of Ministry of Exchange"
                    className="w-full aspect-[3/4] object-cover rounded-2xl block relative z-20"
                    data-testid="founder-photo"
                    loading="lazy"
                  />
                  <div
                    className="mt-4 text-[11px] md:text-xs uppercase tracking-[0.2em] text-muted font-bold"
                    data-testid="founder-caption"
                  >
                   . Nikhilesh, Founder
                  </div>
                </Reveal>

                {/* Copy */}
                <div className="flex-1 min-w-0">
                  <Reveal delay={140}>
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-[-0.03em] leading-[1.05] text-ink">
                      I've walked into 560 of these campuses.
                    </h2>
                  </Reveal>
                  <Reveal delay={220}>
                    <p className="mt-6 text-base md:text-lg text-muted leading-relaxed max-w-xl">
                      About a third are ranked by QS or the Financial Times. Built in Berlin. First-hand, not researched secondhand.
                    </p>
                  </Reveal>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================ 7b · DELIBERATE DARK SECTION (iter58) ================
           Single ink-backgrounded block right before the closing yellow CTA.
           Uses colors already in the palette (moe-ink + moe-paper). Content is
           deliberately minimal. One short trust/precision line. And does not
           duplicate any copy from elsewhere on the page. */}
      <section
        className="py-16 md:py-24 border-t border-ink/10"
        data-testid="dark-precision-section"
        style={{ background: "var(--moe-ink)", color: "var(--moe-paper)" }}
      >
        <div className="max-w-4xl mx-auto w-full px-6 text-center">
          <Reveal>
            <div
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-bold"
              style={{ color: "rgba(251,250,245,0.55)" }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--moe-yolk)" }} />
              How we work
            </div>
          </Reveal>
          <Reveal delay={80}>
            <p className="mt-6 text-2xl md:text-3xl lg:text-4xl font-bold tracking-[-0.02em] leading-[1.15]">
              Sourced from official portals. Dated. Reverified. If a fact isn't verifiable, we don't publish it.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ================ 8 · CLOSING CTA (yellow block. Compact) ================ */}
      <section className="py-14 md:py-20 px-6 border-t border-ink/10">
        <div className="max-w-5xl mx-auto">
          <Reveal y={16}>
            <div className="moe-panel-yolk py-12 md:py-16 px-6 text-center rounded-2xl">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-[-0.03em] leading-[1.05]">
                Your seat's confirmed. Your visa isn't. Yet.
              </h2>
              <div className="mt-8">
                <Link to="/start" data-testid="closing-cta" className="moe-btn moe-btn-primary !px-8 !py-3.5 text-base">
                  Start <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Footer rendered globally by App.js */}
    </div>
  );
}
